import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import {
  MapPin,
  Clock,
  Calendar,
  Users,
  UserCheck,
  CalendarOff,
  ClipboardList,
  Download,
  FileText,
  Home,
  Clipboard,
  Briefcase,
  Cake,
  Gift,
} from "lucide-react";
import { useAppSelector } from "@/store/store";
import Chart from "react-apexcharts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";
import { DashboardService } from "@/services/Dashboard/DashboardService";
import type { IGetDeshboardResponse } from "@/interface/dashboard.interface";
import { ThrowError } from "@/api/callApi";
import RenderHolidaysTable from "./DashboardComponents/RenderHolidaysTable";
import RenderEventTable from "./DashboardComponents/RenderEventTable";
import RenderLeaveTable from "./DashboardComponents/RenderLeaveTable";
import PageLoader from "@/components/common/PageLoader";
import Card from "@/components/common/Card";

const genderChartOptions = {
  chart: { type: "donut", fontFamily: "inherit" },
  labels: ["Male", "Female"],
  colors: ["#3b82f6", "#ec4899"],
  legend: { position: "bottom" as const },
  plotOptions: { pie: { donut: { size: "70%" } } },
  dataLabels: { enabled: false },
  stroke: { show: false },
};
const genderChartSeries = [65, 35];

const designationChartOptions = {
  chart: {
    type: "bar",
    toolbar: { show: false },
    fontFamily: "inherit",
    parentHeightOffset: 0,
  },
  plotOptions: {
    bar: { borderRadius: 4, columnWidth: "50%", distributed: true },
  },
  colors: ["#8b5cf6", "#3b82f6", "#10b981", "#f59e0b", "#ef4444"],
  dataLabels: { enabled: false },
  xaxis: {
    categories: ["Manager", "Developer", "Designer", "HR", "Sales"],
    axisBorder: { show: false },
    axisTicks: { show: false },
    labels: { style: { colors: "#64748b" } },
  },
  yaxis: { show: false },
  grid: {
    strokeDashArray: 4,
    yaxis: { lines: { show: true } },
    padding: { top: 0, right: 0, bottom: 0, left: 10 },
  },
  legend: { show: false },
};
const designationChartSeries = [{ name: "Staff", data: [4, 12, 5, 2, 7] }];

const quickActionsBase = [
  {
    id: "leave",
    title: "Apply Leave",
    badge: "0 Pending",
    icon: Calendar,
    gradient: "from-emerald-400 to-emerald-600",
    shadowColor: "shadow-emerald-500/30",
    textColor: "text-emerald-600",
    badgeBg: "bg-emerald-50",
    badgeBorder: "border-emerald-100",
    badgeText: "text-emerald-600",
    canApply: false,
  },
  {
    id: "regularisation",
    title: "Regularisation",
    badge: "0 Request",
    icon: Clock,
    gradient: "from-amber-400 to-amber-600",
    shadowColor: "shadow-amber-500/30",
    textColor: "text-amber-600",
    badgeBg: "bg-amber-50",
    badgeBorder: "border-amber-100",
    badgeText: "text-amber-600",
    canApply: false,
  },
  {
    id: "wfh",
    title: "Work from Home",
    badge: "Apply Now",
    icon: MapPin,
    gradient: "from-blue-500 to-blue-600",
    shadowColor: "shadow-blue-500/30",
    textColor: "text-blue-600",
    badgeBg: "bg-blue-50",
    badgeBorder: "border-blue-100",
    badgeText: "text-blue-600",
    canApply: false,
  },
];

const leaveData = [
  { id: 1, name: "Earned Leave", used: 12, total: 15 },
  { id: 2, name: "Casual Leave", used: 8, total: 12 },
  { id: 3, name: "Sick Leave", used: 10, total: 12 },
];

const SuperAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const userDetail = useAppSelector((state) => state.user.user);
  const [dashboardData, setDashboardData] =
    useState<IGetDeshboardResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    DashboardService.getDashboard(today)
      .then((res) => {
        setDashboardData(res);
        setLoading(false);
      })
      .catch((err) => {
        ThrowError(err);
        setLoading(false);
      });
  }, []);

  const data = dashboardData?.data;
  const cards = data?.cards;
  const userCards = data?.userCards;
  const recentLeaves = data?.recentLeaveRequests || [];
  const myLeaves = recentLeaves.filter((l) => l.employee.id === userDetail?.id);
  const upcoming = data?.upcoming;

  const breakdown = data?.pendingApprovalBreakdown;
  const regularization = breakdown?.regularization ?? 0;
  const outdoorDuty = breakdown?.outdoorDuty ?? 0;
  const wfh = breakdown?.wfh ?? 0;
  const leave = breakdown?.leave ?? 0;

  const maxBreakdown = Math.max(regularization, outdoorDuty, wfh, leave, 1);
  const totalPending = regularization + outdoorDuty + wfh + leave;

  const upcomingEvents = [
    ...(upcoming?.birthdays || []),
    ...(upcoming?.workAnniversaries || []),
  ].sort((a, b) => (a.daysLeft ?? 0) - (b.daysLeft ?? 0));
  const birthdays = upcomingEvents.filter((e) => e.type === "birthday");
  const anniversaries = upcomingEvents.filter(
    (e) => e.type === "workAnniversary",
  );
  const holidays = upcoming?.holidays || [];

  const isAdmin =
    userDetail?.designation?.level === 1 ||
    userDetail?.designation?.level === 0;

  const getQuickActions = () => {
    if (!dashboardData) return quickActionsBase;
    const { userCards } = dashboardData.data;
    return quickActionsBase.map((item) => {
      switch (item.id) {
        case "leave":
          return { ...item, badge: `${userCards.applyLeave.pending} Pending` };
        case "regularisation":
          return {
            ...item,
            badge: `${userCards.regularisation.requests} Request`,
          };
        case "wfh": {
          const { pending, canApply } = userCards.workFromHome;
          return { ...item, badge: `Pending: ${pending}`, canApply };
        }
        default:
          return item;
      }
    });
  };
  const quickActions = getQuickActions();

  const handleCardClick = (id: string) => {
    const routes: Record<string, string> = {
      leave: RoutesLink.myLeave,
      regularisation: RoutesLink.myRegularization,
      wfh: RoutesLink.wfhRequest,
    };
    if (routes[id]) navigate(routes[id]);
  };

  if (loading) {
    return (
      <Layout>
        <PageLoader />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading title="Dashboard" />

        {!isAdmin ? (
          // ─────────────────────────────────────────────
          // Dashboard for Employees
          // ─────────────────────────────────────────────
          <section className="space-y-6">

            {/* ── Row 1: Quick Action Cards (3 columns) ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {quickActions.map((item) => {
                const Icon = item.icon;
                const isWfh = item.id === "wfh";
                const canApply = item.canApply;
                return (
                  <div
                    key={item.id}
                    onClick={() => handleCardClick(item.id)}
                    className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1 cursor-pointer"
                  >
                    <div
                      className={`absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 transform group-hover:scale-110 ${item.textColor}`}
                    >
                      <Icon size={100} />
                    </div>
                    <div className="relative z-10 flex flex-col h-full gap-4 justify-between">
                      <div
                        className={`w-12 h-12 rounded-2xl bg-linear-to-br ${item.gradient} text-white flex items-center justify-center shadow-lg ${item.shadowColor}`}
                      >
                        <Icon className="w-6 h-6" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-slate-800">
                          {item.title}
                        </h3>
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <span
                            className={`text-[13px] font-medium ${item.badgeBg} ${item.badgeText} px-2.5 py-0.5 rounded-full border ${item.badgeBorder}`}
                          >
                            {item.badge}
                          </span>
                          {isWfh && (
                            <span
                              className={`text-[13px] font-medium px-2.5 py-0.5 rounded-full border ${canApply ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}
                            >
                              {canApply ? "Can Apply" : "Closed"}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* ── Row 2: Leave Balance (full width, horizontal layout) ── */}
            <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <h3 className="font-display text-sm font-semibold text-slate-800">
                  Leave Balance
                </h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 p-5">
                {leaveData.map((item) => {
                  const pct = (item.used / item.total) * 100;
                  const near = pct > 80;
                  return (
                    <div key={item.id} className="space-y-1.5">
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-500 font-medium">
                          {item.name}
                        </span>
                        <span
                          className={`text-sm font-semibold ${near ? "text-amber-600" : "text-slate-800"}`}
                        >
                          {item.used} / {item.total}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full transition-all duration-500 ${near ? "bg-amber-500" : "bg-emerald-500"}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ── Row 3: My Recent Leave Requests (full width) ── */}
            <RenderLeaveTable
              title="My Recent Leave Requests"
              leaves={myLeaves}
              detailPath={RoutesLink.leaveDetail}
            />

            {/* ── Row 4: Birthdays & Anniversaries (2 columns) ── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <RenderEventTable
                title="Upcoming Birthdays"
                events={birthdays}
                icon={<Cake size={16} className="text-pink-500" />}
                iconBg="bg-pink-50"
                iconColor="text-pink-500"
              />
              <RenderEventTable
                title="Upcoming Work Anniversaries"
                events={anniversaries}
                icon={<Gift size={16} className="text-violet-500" />}
                iconBg="bg-violet-50"
                iconColor="text-violet-500"
              />
            </div>
          </section>
        ) : (
          // ─────────────────────────────────────────────
          // Dashboard for Super Admin
          // ─────────────────────────────────────────────
          <section className="space-y-6">

            {/* ── Row 1: Top Stat Cards (4 columns) ── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {/* Total Employees */}
              <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 transform group-hover:scale-110 text-blue-600">
                  <Users size={100} />
                </div>
                <div className="relative z-10 flex flex-col h-full justify-between gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-500/30">
                    <Users className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[13px] font-bold text-slate-500 uppercase tracking-wider">
                      Total Employees
                    </div>
                    <div className="text-4xl font-display font-bold text-slate-800">
                      {cards?.totalEmployees ?? 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Present Today */}
              <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 transform group-hover:scale-110 text-emerald-600">
                  <UserCheck size={100} />
                </div>
                <div className="relative z-10 flex flex-col h-full justify-between gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <UserCheck className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[13px] font-bold text-slate-500 uppercase tracking-wider">
                      Present Today
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="text-4xl font-display font-bold text-slate-800">
                        {cards?.presentToday?.count ?? 0}
                      </span>
                      <span className="text-sm font-semibold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                        of {cards?.presentToday?.total ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* On Leave */}
              <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 transform group-hover:scale-110 text-sky-600">
                  <CalendarOff size={100} />
                </div>
                <div className="relative z-10 flex flex-col h-full justify-between gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-sky-400 to-sky-600 text-white flex items-center justify-center shadow-lg shadow-sky-500/30">
                    <CalendarOff className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[13px] font-bold text-slate-500 uppercase tracking-wider">
                      On Leave
                    </div>
                    <div className="text-4xl font-display font-bold text-slate-800">
                      {cards?.onLeave ?? 0}
                    </div>
                  </div>
                </div>
              </div>

              {/* Pending Approvals */}
              <div className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
                <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity duration-300 transform group-hover:scale-110 text-amber-600">
                  <ClipboardList size={100} />
                </div>
                <div className="relative z-10 flex flex-col h-full justify-between gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-linear-to-br from-amber-400 to-amber-600 text-white flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <ClipboardList className="w-6 h-6" />
                  </div>
                  <div className="space-y-1">
                    <div className="text-[13px] font-bold text-slate-500 uppercase tracking-wider">
                      Pending Approvals
                    </div>
                    <div className="text-4xl font-display font-bold text-slate-800">
                      {cards?.pendingApprovals ?? 0}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Row 2: My Pending Requests (3 columns) ── */}
            <div className="space-y-3">
              <h3 className="font-display text-[15.5px] font-semibold text-slate-800">
                My Pending Requests
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                <div
                  onClick={() => navigate(RoutesLink.myLeave)}
                  className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500" />
                  <div className="w-10 h-10 rounded-xl bg-violet-50 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                    <FileText className="w-5 h-5 text-violet-600" />
                  </div>
                  <div className="text-[11.5px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    My Leave Pending
                  </div>
                  <div className="text-4xl font-display font-bold text-slate-800 tabular-nums">
                    {userCards?.applyLeave?.pending ?? 0}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-400">
                    {(userCards?.applyLeave?.pending ?? 0) === 0
                      ? "All caught up"
                      : "Awaiting approval"}
                  </div>
                </div>

                <div
                  onClick={() => navigate(RoutesLink.myRegularization)}
                  className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-500" />
                  <div className="w-10 h-10 rounded-xl bg-amber-50 text-white flex items-center justify-center mb-4 shadow-md shadow-amber-500/20">
                    <Clipboard className="w-5 h-5 text-amber-600" />
                  </div>
                  <div className="text-[11.5px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    My Regularisation Requests
                  </div>
                  <div className="text-4xl font-display font-bold text-slate-800 tabular-nums">
                    {userCards?.regularisation?.requests ?? 0}
                  </div>
                  <div className="mt-1.5 text-xs text-slate-400">
                    {(userCards?.regularisation?.requests ?? 0) === 0
                      ? "No requests"
                      : "Awaiting approval"}
                  </div>
                </div>

                <div
                  onClick={() => navigate(RoutesLink.wfhRequest)}
                  className="relative overflow-hidden bg-white border border-slate-200/80 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer sm:col-span-2 lg:col-span-1"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500" />
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-white flex items-center justify-center mb-4 shadow-md shadow-blue-500/20">
                    <Home className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="text-[11.5px] font-bold text-slate-500 uppercase tracking-wider mb-2">
                    My WFH Pending
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-4xl font-display font-bold text-slate-800 tabular-nums">
                      {userCards?.workFromHome?.pending ?? 0}
                    </span>
                    <span
                      className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${userCards?.workFromHome?.canApply
                        ? "text-green-700 bg-green-50"
                        : "text-red-700 bg-red-50"
                        }`}
                    >
                      {userCards?.workFromHome?.canApply
                        ? "Can Apply"
                        : "Closed"}
                    </span>
                  </div>
                  <div className="mt-1.5 text-xs text-slate-400">
                    {(userCards?.workFromHome?.pending ?? 0) === 0
                      ? "No pending"
                      : "Awaiting manager sign-off"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── Row 3: Pending Approval Breakdown + Holidays (2 columns) ── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {/* Pending Approval Breakdown Card */}
              <Card>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-display text-[15.5px] font-semibold text-slate-800">
                    Pending Approval Breakdown
                  </h3>
                  <span className="text-xs text-slate-500">
                    Total pending{" "}
                    <b className="text-slate-800 tabular-nums">
                      {totalPending}
                    </b>
                  </span>
                </div>

                <div className="flex flex-col">
                  <div className="grid grid-cols-[36px_110px_1fr_32px] sm:grid-cols-[36px_120px_1fr_32px] items-center gap-3 py-2.5 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                      <Clipboard className="w-4 h-4 text-amber-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 truncate">
                      Regularization
                    </span>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-amber-500"
                        style={{
                          width: `${(regularization / maxBreakdown) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                      {regularization}
                    </span>
                  </div>

                  <div className="grid grid-cols-[36px_110px_1fr_32px] sm:grid-cols-[36px_120px_1fr_32px] items-center gap-3 py-2.5 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 truncate">
                      Outdoor Duty
                    </span>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-teal-500"
                        style={{
                          width: `${(outdoorDuty / maxBreakdown) * 100}%`,
                        }}
                      />
                    </div>
                    <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                      {outdoorDuty}
                    </span>
                  </div>

                  <div className="grid grid-cols-[36px_110px_1fr_32px] sm:grid-cols-[36px_120px_1fr_32px] items-center gap-3 py-2.5 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
                      <Home className="w-4 h-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 truncate">
                      WFH
                    </span>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-blue-500"
                        style={{ width: `${(wfh / maxBreakdown) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                      {wfh}
                    </span>
                  </div>

                  <div className="grid grid-cols-[36px_110px_1fr_32px] sm:grid-cols-[36px_120px_1fr_32px] items-center gap-3 py-2.5">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                      <FileText className="w-4 h-4 text-violet-600" />
                    </div>
                    <span className="text-sm font-semibold text-slate-700 truncate">
                      Leave
                    </span>
                    <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-violet-500"
                        style={{ width: `${(leave / maxBreakdown) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-slate-800 text-right tabular-nums">
                      {leave}
                    </span>
                  </div>
                </div>
              </Card>
              <RenderHolidaysTable holidays={holidays} />
            </div>

            {/* ── Row 4: Recent Leave Requests (full width) ── */}
            <RenderLeaveTable
              title="My Recent Leave Requests"
              leaves={myLeaves}
              detailPath={RoutesLink.leaveDetail}
            />

            {/* ── Row 5: Birthdays & Anniversaries (2 columns) ── */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              <RenderEventTable
                title="Upcoming Birthdays"
                events={birthdays}
                icon={<Cake size={16} className="text-pink-500" />}
                iconBg="bg-pink-50"
                iconColor="text-pink-500"
              />
              <RenderEventTable
                title="Upcoming Work Anniversaries"
                events={anniversaries}
                icon={<Gift size={16} className="text-violet-500" />}
                iconBg="bg-violet-50"
                iconColor="text-violet-500"
              />
            </div>

            {/* ── Row 6: Charts — Designation Bar + Gender Donut (2:1 ratio) ── */}
            <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
              <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <h3 className="font-display text-sm font-semibold text-slate-800">
                    Staff by Designation Level
                  </h3>
                  <button className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                    <Download size={16} /> Download
                  </button>
                </div>
                <div className="p-5">
                  <Chart
                    options={designationChartOptions as any}
                    series={designationChartSeries}
                    type="bar"
                    height={260}
                  />
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <h3 className="font-display text-sm font-semibold text-slate-800">
                    Gender Distribution
                  </h3>
                  <button className="text-xs text-sky-600 hover:underline inline-flex items-center gap-1">
                    <Download size={16} /> Download
                  </button>
                </div>
                <div className="p-5">
                  <Chart
                    options={genderChartOptions as any}
                    series={genderChartSeries}
                    type="donut"
                    height={260}
                  />
                </div>
              </div>
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;